import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Modal from './Modal';
import ToastContainer from './ToastContainer';
import {useToast} from '../hooks/useToast';
import useActorNames from '../hooks/useActorNames';
import useProjectAccess from '../hooks/useProjectAccess';
import vfsClient from '../utils/vfsClient';
import indexedDbFileStorage from '../utils/indexedDbFileStorage';
import {changedServiceKeys} from '../utils/changedServices';
import './HistoryPanel.css';
import './VersionsPanel.css';

/**
 * VersionsPanel — the app's versions (History › Changes › Versions): named points of the code of every service
 * ("v3", "Launch", "1.2.0"), saved by hand here or by Publish, newest first. The one the code is now says "Now". An
 * editor saves the code as it is as a version, goes back to one (every service to that version, as new commits:
 * nothing in the history is lost, services added later stay, and Undo puts everything back as it was), or deletes one
 * (its commits stay). A viewer sees the list. vfs-service: src/history/versions.js (vfsClient listVersions…).
 */

const DEPLOY_WHERE = {'console-next': 'Console', gitlab: 'GitLab', github: 'GitHub', zip: 'a zip', git: 'Git'};
const repoName = (repo) => String(repo || '').replace(/^services\//, '');

/** "Published to GitLab" / "Saved by hand". */
export function versionOrigin(version) {
  if (version?.trigger !== 'publish') { return 'Saved'; }
  const where = DEPLOY_WHERE[version?.deploy?.kind] || version?.deploy?.kind;
  return where ? `Published to ${where}` : 'Published';
}

/** What going back did, in one line. */
export function restoreSummary(result) {
  const files = result?.restoredPaths?.length || 0;
  const name = result?.version?.name || 'the version';
  const left = (result?.untouched || []).map((u) => repoName(u.repo));
  const base = files ? `Back to ${name}: ${files} file${files === 1 ? '' : 's'} restored` : `Already at ${name}: nothing to change`;
  return left.length ? `${base}. Left as ${left.length === 1 ? 'it is' : 'they are'}: ${left.join(', ')}` : base;
}

function formatWhen(iso) {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : at.toLocaleString();
}

const VersionsPanel = ({projectId}) => {
  const {toasts, showToast, removeToast} = useToast();
  const actorOf = useActorNames(projectId);
  const {access} = useProjectAccess(projectId);
  const canEdit = !access || access.role !== 'viewer';

  const [versions, setVersions] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind: 'restore'|'delete', version}
  const [busy, setBusy] = useState(false);
  const [undo, setUndo] = useState(null); // {targets, label}
  const [lastSaved, setLastSaved] = useState(null); // {id, name}: the version just saved (no name asked: say which)

  const load = useCallback(async () => {
    if (!projectId) { return; }
    setLoadError(null);
    try {
      const answer = await vfsClient.listVersions(projectId);
      setVersions(Array.isArray(answer?.versions) ? answer.versions : []);
    } catch (error) {
      setLoadError(error.status === 404 && !error.code
        ? 'Your sandbox runs an older version without app versions: put it to sleep from the Sandbox page and open the project again.'
        : error.message || 'The versions could not be loaded');
    }
  }, [projectId]);

  useEffect(() => { setVersions(null); setUndo(null); setLastSaved(null); load(); }, [load]);

  // The editor's cache of every service a restore changed (as a rewind does), so open files show it.
  const refreshServices = async (commits) => {
    // By the service's path (`services/web`), the key of the editor's cache: a bare `web` is a 404 on GET /tree.
    for (const service of changedServiceKeys(commits)) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const files = await vfsClient.fetchServiceFiles(projectId, service);
        for (const [relPath, content] of Object.entries(files)) {
          // eslint-disable-next-line no-await-in-loop
          await indexedDbFileStorage.saveFile(projectId, service, relPath, content, {fromVfs: true});
        }
        window.dispatchEvent(new CustomEvent('gitPullCompleted', {detail: {projectId, serviceName: service, success: true}}));
      } catch {
        showToast(`The files of ${repoName(service)} could not be refreshed in the editor: reload to see them.`, 'warning');
      }
    }
  };

  const save = async (event) => {
    event?.preventDefault();
    if (saving) { return; }
    setSaving(true);
    try {
      const {version} = await vfsClient.saveVersion(projectId, {name: name.trim(), note: note.trim()});
      showToast(`Saved as version ${version.name}`, 'success');
      setLastSaved({id: version.id, name: version.name});
      setName('');
      setNote('');
      setUndo(null);
      await load();
    } catch (error) {
      if (error.code === 'unchanged') {
        showToast(`Nothing changed since version ${error.version?.name || 'the last one'}`, 'info');
      } else if (error.code === 'nothing') {
        showToast('There is no code to save as a version yet', 'info');
      } else {
        showToast(`The version could not be saved: ${error.message}`, 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    if (!confirm || busy) { return; }
    const {kind, version} = confirm;
    setBusy(true);
    try {
      if (kind === 'restore') {
        setLastSaved(null);
        const result = await vfsClient.restoreVersion(projectId, version.id);
        await refreshServices(result.commits);
        showToast(restoreSummary(result), 'success');
        const previous = result?.previous && Object.keys(result.previous).length ? result.previous : null;
        setUndo(previous && (result.restoredPaths || []).length ? {targets: previous, label: version.name} : null);
      } else {
        await vfsClient.deleteVersion(projectId, version.id);
        showToast(`Version ${version.name} deleted (its code stays in the history)`, 'success');
      }
      setConfirm(null);
      await load();
    } catch (error) {
      showToast(`${kind === 'restore' ? 'Going back' : 'Deleting'} failed: ${error.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const runUndo = async () => {
    if (!undo || busy) { return; }
    setBusy(true);
    try {
      const result = await vfsClient.rollbackAll(projectId, Object.values(undo.targets)[0], {targets: undo.targets});
      await refreshServices(result.commits);
      showToast('Undone: the code is as it was before', 'success');
      setUndo(null);
      await load();
    } catch (error) {
      showToast(`Undo failed: ${error.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="history-panel versions-panel" data-testid="versions-panel">
      {canEdit && (
        <form className="history-panel-checkpoint versions-save" onSubmit={save}>
          <input
            aria-label="Version name"
            className="history-checkpoint-input versions-name"
            disabled={saving}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name (optional, e.g. Launch)"
            type="text"
            value={name}
          />
          <input
            aria-label="What this version is"
            className="history-checkpoint-input"
            disabled={saving}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What it is (optional)"
            type="text"
            value={note}
          />
          <button className="history-checkpoint-btn" disabled={saving || !projectId} type="submit">
            {saving ? 'Saving…' : 'Save a version'}
          </button>
        </form>
      )}
      {lastSaved && (
        <div className="versions-saved" data-testid="versions-saved" role="status">
          Saved as <strong>{lastSaved.name}</strong>
        </div>
      )}
      {undo && (
        <div className="versions-undo" role="status">
          <span>The code went back to {undo.label}.</span>
          <button className="history-redo-btn" disabled={busy} onClick={runUndo} type="button">Undo</button>
        </div>
      )}
      <p className="versions-hint">
        A version is the code of every service at one moment: saved here, or each time the app is published.
        Going back keeps the history: it adds new changes, and Undo puts everything back.
      </p>

      {!versions && !loadError && <div className="history-panel-status">Loading versions…</div>}
      {loadError && <div className="history-panel-status history-panel-error">{loadError}</div>}
      {versions && versions.length === 0 && (
        <div className="history-panel-status">No versions yet.{canEdit ? ' Save one now, or publish the app.' : ''}</div>
      )}
      {versions && versions.length > 0 && (
        <ul className="history-commit-list" aria-label="Versions">
          {versions.map((version) => {
            const by = version.by?.actorId ? actorOf(version.by.actorId) : null;
            const services = Object.keys(version.repos || {}).map(repoName);
            return (
              <li className={`history-commit-item versions-item${lastSaved?.id === version.id ? ' is-just-saved' : ''}`} data-testid="version-row" key={version.id}>
                <div className="history-commit-main versions-main">
                  <span className="history-commit-message">
                    <strong>{version.name}</strong>
                    {version.note ? <span className="versions-note"> — {version.note}</span> : null}
                  </span>
                  <span className="history-commit-meta">
                    <span>{versionOrigin(version)}</span>
                    {by && <span className="history-commit-actor" title={by.email || undefined}>{by.name}</span>}
                    <span title={version.createdAt}>{formatWhen(version.createdAt)}</span>
                    {services.length > 0 && <span>{services.join(', ')}</span>}
                  </span>
                  {version.missing?.length > 0 && (
                    <span className="versions-missing">No longer in the project: {version.missing.map(repoName).join(', ')}</span>
                  )}
                </div>
                {lastSaved?.id === version.id && <span className="versions-just-saved">Just saved</span>}
                {version.current && <span className="history-current-badge">Now</span>}
                {canEdit && !version.current && (
                  <button className="history-rewind-btn" onClick={() => setConfirm({kind: 'restore', version})} type="button">
                    Go back to this version
                  </button>
                )}
                {canEdit && (
                  <button
                    aria-label={`Delete version ${version.name}`}
                    className="history-rewind-btn versions-delete"
                    onClick={() => setConfirm({kind: 'delete', version})}
                    type="button"
                  >
                    Delete
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <Modal
        cancelText="Cancel"
        confirmText={confirm?.kind === 'delete' ? 'Delete' : 'Go back'}
        isDangerous
        isLoading={busy}
        isOpen={Boolean(confirm)}
        message={confirm ? (confirm.kind === 'delete'
          ? `Delete version ${confirm.version.name}? Its code stays in the project's history; only the name goes.`
          : `Put every service back to version ${confirm.version.name} (${formatWhen(confirm.version.createdAt)})? This adds new changes on top: nothing in the history is lost, what is not saved yet is saved first, services added later stay as they are, and you can undo it.`) : ''}
        onCancel={() => !busy && setConfirm(null)}
        onConfirm={runConfirmed}
        title={confirm?.kind === 'delete' ? 'Delete this version?' : 'Go back to this version?'}
        type="confirm"
      />
      <ToastContainer onRemoveToast={removeToast} toasts={toasts} />
    </div>
  );
};

VersionsPanel.propTypes = {
  projectId: PropTypes.string.isRequired,
};

export default VersionsPanel;
